/**
 * (b3) · EL AVISO VISIBLE DE PENDIENTES VERI*FACTU — textos de Lara, 01-10-2026.
 *
 * Sostiene la frase del **1.d** —«mantiene visible un aviso con el número de registros
 * pendientes»— que exige el **art. 16.4** de la Orden HAC/1177/2024.
 *
 * ## 🔴 Por qué `Popover` y NO `Tooltip`
 *
 * El requisito es literal: **«se abre con clic, teclado y toque»**. Un tooltip se abre al
 * pasar el ratón —así que **en una tableta no se abre nunca**— y su contenido no recibe
 * foco. Un popover tiene por disparador un **botón de verdad**: clic, `Enter`/`Espacio` y
 * toque son el mismo gesto para el navegador. Elegir el componente ES el requisito, no un
 * detalle de implementación.
 *
 * ## 🔴 Por qué nunca se pinta un «0»
 *
 * El aviso desaparece **solo si las tres cifras son cero**. Si el grupo 1 está a cero y hay
 * algo en «requiere tu actuación» o en «en revisión», el aviso **sigue** y pinta un
 * **símbolo**: un «0» diría «no hay nada», y habría algo. Las **cifras exactas** van dentro,
 * en el emergente y en la tarjeta.
 *
 * ## 🔴 Este componente NO tiene ni un literal, y es el cambio del 14-10-2026
 *
 * Los rótulos, la frase emergente, la línea de qué hacer y la señal de incidencia llegan
 * **de `/api/verifactu-records/stats`**, ya compuestos y ya decididos. Había una copia de
 * los seis literales aquí (`textosAvisoPendientes.js`, retirado), y el que Lara corrigió el
 * 01-10 se aplicó en la copia de la API y **no** en esta: el caso de «incidencia activa y
 * nada pendiente» se pintó trece días con el rótulo equivocado y **nada se puso rojo**.
 *
 * Tampoco se decide nada: `avisoVisible`, `avisoSinCifra`, el `null` de la línea de qué
 * hacer y cuál de las dos señales de incidencia toca vienen resueltos. Lo único que se hace
 * aquí es pintar.
 *
 * ## 🔴 La señal depende de la CAUSA, y la causa la sirve la API (`senal_causa`)
 *
 * Una incidencia **técnica de VERIX**, una **retención deliberada** y una de **comunicación con
 * la AEAT** le dicen cosas distintas al cliente: la primera la atiende VERIX, la segunda es
 * temporal y decidida por una persona, y la tercera es la de siempre. `senal_causa` llega **ya
 * resuelta** —causa, título y texto con el recuento dentro— y aquí no se elige la causa ni se
 * interpola ningún `{N}`: **un recuento interpolado en el cliente es un recuento que nadie ha
 * medido.**
 *
 * 📌 `data-causa` lleva la causa servida, así que **una causa nueva se pinta sin tocar este
 * componente**: en la API se añade su texto y aquí no hay nada que añadir.
 */
import { AlertTriangle, FileClock } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { cn } from '@/lib/utils';

/** El símbolo que sustituye a la cifra. Nunca un «0». */
export const SIMBOLO_SIN_CIFRA = '•';

/**
 * ¿Se pinta el aviso? Las DOS condiciones, en un solo sitio.
 *
 * `avisoVisible` lo decide el servidor. La segunda —que haya traído sus textos— es una
 * renuncia nuestra, y está escrita aquí porque la comparten las TRES superficies (el aviso,
 * la hamburguesa de `TopBar` y el badge de `Sidebar`): si cada una la resolviera por su
 * cuenta, una de ellas pintaría un badge sin nada que decir.
 *
 * 🔴 Y la renuncia es deliberada: un aviso con sus tres cifras y sin rótulos son tres
 * números sin decir de qué, y aquí **no se inventa** el rótulo que falta. El caso solo puede
 * darse con una API anterior a esta tanda, y lo que lo impide es el ORDEN DE MERGE —primero
 * la API, después el ERP—, declarado en el título del PR.
 */
export function avisoSePinta(stats) {
  return !!stats?.avisoVisible && !!stats?.textos;
}

/**
 * Lo que se lee del aviso. Es el texto accesible, y es **el mismo** que el del emergente.
 *
 * 🔴 Se compone con lo que sirvió el servidor, no con una frase propia: lo que oye un lector
 * de pantalla tiene que ser lo mismo que lee quien abre el emergente, y dos redacciones del
 * mismo dato divergen en cuanto alguien toca una.
 */
export function textoAccesibleDelAviso(textos) {
  const senal = senalDelAviso(textos);
  // 🔴 La MISMA señal que se pinta, con su título y su cuerpo. Lo que oye un lector de
  // pantalla tiene que ser lo que lee quien abre el emergente: si aquí se leyera solo el
  // título, el cuerpo —que es donde va el recuento— no existiría para quien no ve.
  return [textos?.emergente, senal?.titulo, senal?.texto].filter(Boolean).join(' ');
}

/**
 * La señal que toca, en UN solo sitio: la de la CAUSA si la API la sirvió, y si no la genérica.
 *
 * 🔴 **Esta precedencia es la ÚNICA decisión que queda en el cliente, y es la que el contrato
 * describe**: el servidor manda `senal_causa: null` cuando la causa no tiene texto propio
 * —incluida `aeat`, que conserva `senal_incidencia` tal como estaba—, así que «hay causa» y
 * «usa la genérica» son estados **servidos**, no deducidos aquí.
 *
 * Y vive en una función y no en el JSX porque la comparten **el texto accesible y lo que se
 * pinta**: con la condición escrita dos veces, un lector de pantalla podría oír una señal y la
 * pantalla mostrar otra — y nada se pondría rojo.
 */
export function senalDelAviso(textos) {
  const causa = textos?.senal_causa;
  if (causa) return { causa: causa.causa ?? null, titulo: causa.titulo, texto: causa.texto ?? null };
  if (textos?.senal_incidencia) return { causa: null, titulo: textos.senal_incidencia, texto: null };
  return null;
}

/**
 * Lo que se pinta DENTRO del badge: la cifra del grupo 1, o el símbolo.
 *
 * `sinCifra` lo decide el servidor. Se tope a `99+` porque un badge de tres dígitos
 * desborda el icono, y el número exacto está en el emergente.
 */
export function contenidoDelBadge({ aviso, avisoSinCifra }) {
  if (avisoSinCifra) return SIMBOLO_SIN_CIFRA;
  return (aviso ?? 0) > 99 ? '99+' : String(aviso ?? 0);
}

export default function AvisoPendientes({ stats, className }) {
  if (!avisoSePinta(stats)) return null;

  const textos = stats.textos;
  const grupos = stats.grupos ?? {};
  const contenido = contenidoDelBadge(stats);
  const senal = senalDelAviso(textos);

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          className={cn('relative shrink-0', className)}
          aria-label={textoAccesibleDelAviso(textos)}
          data-testid="aviso-pendientes-trigger"
        >
          <FileClock className="w-5 h-5" />
          <span
            className="absolute -top-0.5 -right-0.5 min-w-[16px] h-[16px] px-1 inline-flex items-center justify-center rounded-full bg-primary text-primary-foreground text-[9px] font-semibold tabular-nums"
            aria-hidden="true"
            data-testid="aviso-pendientes-cifra"
          >
            {contenido}
          </span>
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-80" data-testid="aviso-pendientes-emergente">
        {/* 🔴 Las TRES cifras EXACTAS, sin tope de 99+: el badge resume, esto no. */}
        <ul className="space-y-1.5 text-sm">
          <li className="flex items-baseline justify-between gap-3">
            <span>{textos.grupo_pendiente_aceptacion}</span>
            <span className="font-semibold tabular-nums" data-testid="cifra-pendiente-aceptacion">
              {grupos.pendienteAceptacion ?? 0}
            </span>
          </li>
          <li className="flex items-baseline justify-between gap-3">
            <span>{textos.grupo_requiere_actuacion}</span>
            <span className="font-semibold tabular-nums" data-testid="cifra-requiere-actuacion">
              {grupos.requiereActuacion ?? 0}
            </span>
          </li>
          <li className="flex items-baseline justify-between gap-3">
            <span>{textos.grupo_en_revision}</span>
            <span className="font-semibold tabular-nums" data-testid="cifra-en-revision">
              {grupos.enRevision ?? 0}
            </span>
          </li>
        </ul>
        {/* La frase que impide sumarlas. Literal de Lara, 01-10-2026. */}
        <p className="mt-2 pt-2 border-t border-border text-xs text-muted-foreground">
          {textos.categorias_solapan}
        </p>
        {/* 🔴 Qué HACER con el grupo 2. La CONDICIÓN la resolvió el servidor: aquí llega
            `null` cuando no hay nada que hacer, y por eso no hay un `> 0` en esta línea.
            Un aviso que pide algo sin decir qué es ruido — y uno que lo dice cuando no hay
            nada que hacer enseña a ignorarlo. */}
        {textos.que_hacer_requiere_actuacion && (
          <p className="mt-2 text-xs text-muted-foreground" data-testid="que-hacer-actuacion">
            {textos.que_hacer_requiere_actuacion}
          </p>
        )}
        {/* 🔴 La señal de envío: icono Y TEXTO. El requisito es que se lea «sin depender del
            color ni del ratón», así que el texto está en el DOM, no en un `title` ni
            insinuado por un color rojo.

            QUÉ dice lo decidió el servidor —la causa con su título y su cuerpo, o la señal
            genérica—; aquí solo queda la precedencia, y vive en `senalDelAviso`. */}
        {senal && (
          <div
            className="mt-2 text-xs text-destructive"
            data-testid="senal-incidencia-envio"
            data-causa={senal.causa ?? undefined}
          >
            <p className="flex items-start gap-1.5 font-medium">
              <AlertTriangle className="w-3.5 h-3.5 shrink-0 mt-px" aria-hidden="true" />
              <span>{senal.titulo}</span>
            </p>
            {/* El cuerpo solo existe cuando la causa lo trae: la señal genérica es una línea. */}
            {senal.texto && <p className="mt-1">{senal.texto}</p>}
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
}
